import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";

import { useGetAccountAccess } from "~/v2/api/hooks";
import type { AccountAccessRole } from "~/v2/api/rest";
import { useAccountContext } from "~/v2/providers/AccountProvider";

export type AccountAccessStatus =
  | "idle"
  | "loading"
  | "ready"
  | "unauthorized"
  | "no_access"
  | "error";

interface AccountAccessContextProps {
  accountID?: string;
  roles: AccountAccessRole[];
  status: AccountAccessStatus;
  isLoading: boolean;
  refetch: () => void;
  hasRole: (role: AccountAccessRole) => boolean;
}

const ACCOUNT_ACCESS_CONTEXT_DEFAULTS: AccountAccessContextProps = {
  accountID: undefined,
  roles: [],
  status: "idle",
  isLoading: false,
  refetch: () => null,
  hasRole: () => false,
};

const PAPER_ACCOUNT_ROLES: AccountAccessRole[] = ["primary"];

export const AccountAccessContext = createContext<AccountAccessContextProps>(
  ACCOUNT_ACCESS_CONTEXT_DEFAULTS,
);

export const AccountAccessProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    currentAccount,
    product,
    isLoading: isAccountLoading,
  } = useAccountContext();
  const accountID = currentAccount?.id;

  const {
    accountAccess,
    error,
    isLoading: isAccessLoading,
    refetch: refetchAccountAccess,
  } = useGetAccountAccess({
    accountID,
    product,
  });

  const status = useMemo<AccountAccessStatus>(() => {
    if (isAccountLoading) {
      return "loading";
    }
    if (!accountID) {
      return "idle";
    }
    if (product === "paper") {
      return "ready";
    }
    if (product !== "live") {
      return "idle";
    }
    if (isAccessLoading) {
      return "loading";
    }
    if (accountAccess) {
      return "ready";
    }

    const errorStatus = getErrorStatus(error);
    if (errorStatus === 401 || errorStatus === 403) {
      return "unauthorized";
    }
    if (errorStatus === 404) {
      return "no_access";
    }
    if (error) {
      return "error";
    }

    return "idle";
  }, [
    accountAccess,
    accountID,
    error,
    isAccessLoading,
    isAccountLoading,
    product,
  ]);

  const roles = useMemo<AccountAccessRole[]>(() => {
    if (status !== "ready") {
      return [];
    }
    if (product === "paper") {
      return PAPER_ACCOUNT_ROLES;
    }
    return accountAccess?.roles ?? [];
  }, [accountAccess?.roles, product, status]);

  const hasRole = useCallback(
    (role: AccountAccessRole) => roles.includes(role),
    [roles],
  );

  const refetch = useCallback(() => {
    if (product === "live" && accountID) {
      void refetchAccountAccess();
    }
  }, [accountID, product, refetchAccountAccess]);

  return (
    <AccountAccessContext.Provider
      value={{
        accountID,
        roles,
        status,
        isLoading: status === "loading",
        refetch,
        hasRole,
      }}
    >
      {children}
    </AccountAccessContext.Provider>
  );
};

export const useAccountAccessContext = () => useContext(AccountAccessContext);

const getErrorStatus = (error: unknown) => {
  return (error as { response?: { status?: number } } | undefined)?.response
    ?.status;
};
